"use client";

import { useEffect } from "react";
import { useSession } from "next-auth/react";

import { AUTHZ_IDLE_REFRESH_MS } from "@/constants/auth";
import { refreshSessionAuthz } from "@/lib/auth/refresh-session-authz";
import {
  readLastSessionActivityAt,
  shouldRefreshAuthzAfterIdle,
  writeLastSessionActivityAt,
} from "@/lib/auth/session-authz-idle";

export function SessionAuthzRefresh() {
  const { status } = useSession();

  useEffect(() => {
    if (status !== "authenticated") return;

    const maybeRefreshAfterIdle = () => {
      const now = Date.now();
      const lastActivityAt = readLastSessionActivityAt();
      if (shouldRefreshAuthzAfterIdle(now, lastActivityAt, AUTHZ_IDLE_REFRESH_MS)) {
        void refreshSessionAuthz();
      }
      writeLastSessionActivityAt(now);
    };

    maybeRefreshAfterIdle();

    const stampActivity = () => writeLastSessionActivityAt(Date.now());

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        stampActivity();
        return;
      }
      maybeRefreshAfterIdle();
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        maybeRefreshAfterIdle();
      } else {
        stampActivity();
      }
    };

    window.addEventListener("beforeunload", stampActivity);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("beforeunload", stampActivity);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [status]);

  return null;
}
